import { useContext, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';
import lodash from 'lodash';

import { MessageType } from 'types/api/message.type';

import { reactivationDataService } from 'services/reactivation.servise';

import { ChatContext } from 'contexts/ChatContext';

import { routes } from 'data/routes';

import * as S from './Chat.styles';
import { Message } from './components/Message';

const useChat = (
  customerId: string,
  customerCode: string | null,
  messages?: MessageType[]
) => {
  const ref = useRef<HTMLDivElement>(null);
  const { isVisibleSet, send, isRestoreRequest, isRestoreRequestSet, setIsSent, messagesSet } =
    useContext(ChatContext);

  const { register, watch, reset } = useForm();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [messageSent, messageSentSet] = useState(false);
  const [isModalVisible, isModalVisibleSet] = useState(false);

  const messagesGrouped = lodash.groupBy(messages, (item) =>
    dayjs(item.timestamp).format('dddd, D.M')
  );

  const message = watch('message');

  const messageGroupMapped = Object.entries(messagesGrouped).map(([key, messagesArr]) => (
    <>
      <S.Date textAlign="center" color="restTabText" scale="xs">
        {key}
      </S.Date>

      {messagesArr?.map((item: MessageType) => (
        <Message key={item.id} {...item} isCustomerMessage={!item.sendByCustomer} />
      ))}
    </>
  ));

  const sendMessage = () => {
    send(message);
    reset({ message: '' });
    messageSentSet(true);
    setIsSent(true);
  };

  const onEnterPress = (e: KeyboardEvent) => {
    if (!message) return;

    if (e.key === 'Enter' && e.shiftKey === false) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    if (ref && ref.current) {
      const element = ref.current;

      element?.scrollIntoView();
    }
  }, [ref.current, messages]);

  const restoreCustomer = async () => {
    if (messageSent) {
      await reactivationDataService.reactivate(customerId || customerCode).then(() => {
        toast.success(t('chat.reactivationSent'));
        navigate(routes.myCustomers);
      });

      isRestoreRequestSet(false);
    }
  };

  useEffect(() => {
    if (isRestoreRequest && messageSent) {
      restoreCustomer();
    }
  }, [messageSent, isRestoreRequest]);

  return {
    isVisibleSet,
    register,
    messageGroupMapped,
    onEnterPress,
    ref,
    message,
    sendMessage,
    isModalVisible,
    isModalVisibleSet,
    messageSent,
    isRestoreRequest,
    isRestoreRequestSet,
    setIsSent,
    messagesSet,
  };
};

export { useChat };
